import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col } from 'react-bootstrap';
import { Formik, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import Select from 'react-select';
import { FaTrash } from 'react-icons/fa';
import { selectStylesSm } from '~/Styles/reactSelectStyles';
import {
  buscarGerentesDisponiveisImobiliaria,
  buscarContasDisponiveisImobiliaria,
  cadastrarImobiliaria,
  editarImobiliaria,
} from '~/Services/Service';

const TAMANHO_MAXIMO_LOGO = 6 * 1024 * 1024;
const EXTENSOES_LOGO = /\.(png|jpe?g)$/i;

const validationSchema = Yup.object().shape({
  nome: Yup.string()
    .required('Nome é obrigatório')
    .max(50, 'Máximo 50 caracteres'),
  cor: Yup.string()
    .required('Cor é obrigatória')
    .matches(/^#[0-9A-Fa-f]{6}$/, 'Cor deve estar no formato #RRGGBB'),
  codigo_gerente: Yup.number()
    .typeError('Selecione um gerente')
    .required('Selecione um gerente')
    .min(1, 'Selecione um gerente'),
  conta_imobiliaria: Yup.number()
    .typeError('Selecione uma conta')
    .required('Selecione uma conta')
    .min(1, 'Selecione uma conta'),
  ativo: Yup.boolean(),
  ranking_venda: Yup.boolean(),
  logo: Yup.mixed()
    .nullable()
    .test('extensao', 'Apenas PNG ou JPG', (f) => {
      if (!f) return true;
      return EXTENSOES_LOGO.test(f.name || '');
    })
    .test('tamanho', 'Máximo 6 MB', (f) => {
      if (!f) return true;
      return f.size <= TAMANHO_MAXIMO_LOGO;
    }),
});

const getInitialValues = (imob) => {
  if (imob) {
    return {
      codigo: imob.codigo,
      nome: imob.nome || '',
      cor: imob.cor || '#000000',
      codigo_gerente: imob.codigo_gerente || '',
      conta_imobiliaria:
        imob.conta_imobiliaria != null ? imob.conta_imobiliaria : '',
      ativo: imob.ativo != null ? imob.ativo : true,
      ranking_venda: imob.ranking_venda != null ? imob.ranking_venda : true,
      logo: null,
    };
  }
  return {
    codigo: 0,
    nome: '',
    cor: '#000000',
    codigo_gerente: '',
    conta_imobiliaria: '',
    ativo: true,
    ranking_venda: true,
    logo: null,
  };
};

const ModalImobiliaria = (props) => {
  const { show, modo, imobiliariaInicial, onHide, onSalvar } = props;
  const ehEdicao = modo === 'edicao' && imobiliariaInicial != null;

  const [gerentesOpts, setGerentesOpts] = useState([]);
  const [contasOpts, setContasOpts] = useState([]);
  const [logoPreviewUrl, setLogoPreviewUrl] = useState(null);
  const [acaoLogo, setAcaoLogo] = useState('manter');

  useEffect(() => {
    if (!show) return undefined;
    let revoked = null;
    setAcaoLogo('manter');
    setLogoPreviewUrl(
      ehEdicao && imobiliariaInicial && imobiliariaInicial.url_logo
        ? imobiliariaInicial.url_logo
        : null
    );
    carregarGerentes();
    carregarContas();
    return () => {
      if (revoked) URL.revokeObjectURL(revoked);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, ehEdicao, imobiliariaInicial && imobiliariaInicial.codigo]);

  const carregarGerentes = async () => {
    try {
      const resp = await buscarGerentesDisponiveisImobiliaria();
      const opts = (Array.isArray(resp) ? resp : []).map((g) => ({
        value: g.codigo,
        label: g.nome,
      }));
      setGerentesOpts(opts);
    } catch (err) {
      const mensagem =
        err && err.message
          ? err.message
          : 'Erro ao carregar gerentes disponíveis.';
      toast.error(mensagem);
      setGerentesOpts([]);
    }
  };

  const carregarContas = async () => {
    try {
      const resp = await buscarContasDisponiveisImobiliaria();
      const opts = (Array.isArray(resp) ? resp : []).map((c) => ({
        value: c.codigo,
        label: `${c.codigo} - ${c.apelido}`,
      }));
      setContasOpts(opts);
    } catch (err) {
      const mensagem =
        err && err.message
          ? err.message
          : 'Erro ao carregar contas disponíveis.';
      toast.error(mensagem);
      setContasOpts([]);
    }
  };

  const handleSelecionarLogo = (file, formik) => {
    formik.setFieldValue('logo', file);
    if (logoPreviewUrl && logoPreviewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(logoPreviewUrl);
    }
    if (file) {
      setLogoPreviewUrl(URL.createObjectURL(file));
      setAcaoLogo('substituir');
    } else {
      setLogoPreviewUrl(null);
    }
  };

  const handleRemoverLogo = (formik) => {
    formik.setFieldValue('logo', null);
    if (logoPreviewUrl && logoPreviewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(logoPreviewUrl);
    }
    setLogoPreviewUrl(null);
    setAcaoLogo(ehEdicao ? 'remover' : 'manter');
  };

  const handleSubmit = async (values, formikBag) => {
    try {
      const camposBase = {
        nome: values.nome.trim(),
        cor: values.cor,
        codigo_gerente: Number(values.codigo_gerente),
        conta_imobiliaria: Number(values.conta_imobiliaria),
        ranking_venda: !!values.ranking_venda,
        ativo: !!values.ativo,
      };
      if (ehEdicao) {
        const camposEdicao = {
          ...camposBase,
          codigo: imobiliariaInicial.codigo,
        };
        await editarImobiliaria(camposEdicao, values.logo, acaoLogo);
        toast.success('Imobiliária atualizada com sucesso.');
      } else {
        await cadastrarImobiliaria(camposBase, values.logo);
        toast.success('Imobiliária cadastrada com sucesso.');
      }
      onSalvar();
    } catch (err) {
      const mensagem =
        err && err.message ? err.message : 'Erro ao salvar imobiliária.';
      toast.error(mensagem);
    } finally {
      formikBag.setSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header
        closeButton
        style={{ backgroundColor: '#0747a6', color: '#fff' }}
      >
        <Modal.Title style={{ fontSize: '1rem', color: '#fff' }}>
          {ehEdicao ? 'Editar Imobiliária' : 'Nova Imobiliária'}
        </Modal.Title>
      </Modal.Header>

      <Formik
        initialValues={getInitialValues(imobiliariaInicial)}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {(formik) => {
          const gerenteValue =
            gerentesOpts.find(
              (o) => String(o.value) === String(formik.values.codigo_gerente)
            ) || null;
          const contaValue =
            contasOpts.find(
              (o) => String(o.value) === String(formik.values.conta_imobiliaria)
            ) || null;
          const selectStylesComPortal = {
            ...selectStylesSm,
            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
          };

          return (
            <Form onSubmit={formik.handleSubmit}>
              <Modal.Body>
                {/* Identificação */}
                <fieldset
                  className="border rounded px-3 pb-2 pt-1 mb-3"
                  style={{ borderColor: '#dee2e6' }}
                >
                  <legend
                    className="w-auto px-2 mb-0"
                    style={{
                      fontSize: '0.8rem',
                      fontWeight: 'bold',
                      color: '#495057',
                    }}
                  >
                    Identificação
                  </legend>
                  <Row>
                    <Col md={8}>
                      <Form.Group className="mb-1">
                        <Form.Label as="small" className="mb-0">
                          Nome <span className="text-danger">*</span>
                        </Form.Label>
                        <Field
                          as={Form.Control}
                          type="text"
                          name="nome"
                          size="sm"
                          maxLength={50}
                          isInvalid={
                            formik.touched.nome && !!formik.errors.nome
                          }
                        />
                        <ErrorMessage
                          name="nome"
                          component="div"
                          className="invalid-feedback d-block"
                          style={{ fontSize: '0.75rem' }}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-1">
                        <Form.Label as="small" className="mb-0">
                          Cor <span className="text-danger">*</span>
                        </Form.Label>
                        <div className="d-flex align-items-center">
                          <Form.Control
                            type="color"
                            value={formik.values.cor}
                            onChange={(e) => {
                              formik.setFieldValue('cor', e.target.value);
                            }}
                            size="sm"
                            style={{ width: 50, padding: 2 }}
                          />
                          <Form.Control
                            type="text"
                            value={formik.values.cor}
                            onChange={(e) => {
                              formik.setFieldValue('cor', e.target.value);
                            }}
                            size="sm"
                            maxLength={7}
                            className="ml-2"
                            isInvalid={
                              formik.touched.cor && !!formik.errors.cor
                            }
                          />
                        </div>
                        <ErrorMessage
                          name="cor"
                          component="div"
                          className="invalid-feedback d-block"
                          style={{ fontSize: '0.75rem' }}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </fieldset>

                {/* Vínculos */}
                <fieldset
                  className="border rounded px-3 pb-2 pt-1 mb-3"
                  style={{ borderColor: '#dee2e6' }}
                >
                  <legend
                    className="w-auto px-2 mb-0"
                    style={{
                      fontSize: '0.8rem',
                      fontWeight: 'bold',
                      color: '#495057',
                    }}
                  >
                    Vínculos
                  </legend>
                  <Row>
                    <Col md={7}>
                      <Form.Group className="mb-1">
                        <Form.Label as="small" className="mb-0">
                          Gerente <span className="text-danger">*</span>
                        </Form.Label>
                        <Select
                          options={gerentesOpts}
                          value={gerenteValue}
                          onChange={(opt) => {
                            formik.setFieldValue(
                              'codigo_gerente',
                              opt ? opt.value : ''
                            );
                          }}
                          styles={selectStylesComPortal}
                          menuPortalTarget={document.body}
                          menuPosition="fixed"
                          placeholder="Selecione um Assessor disponível..."
                          isClearable
                          noOptionsMessage={() =>
                            'Nenhum gerente disponível'
                          }
                        />
                        {formik.touched.codigo_gerente &&
                          formik.errors.codigo_gerente && (
                            <div
                              className="text-danger"
                              style={{ fontSize: '0.75rem' }}
                            >
                              {formik.errors.codigo_gerente}
                            </div>
                          )}
                      </Form.Group>
                    </Col>
                    <Col md={5}>
                      <Form.Group className="mb-1">
                        <Form.Label as="small" className="mb-0">
                          Conta Imobiliária <span className="text-danger">*</span>
                        </Form.Label>
                        <Select
                          options={contasOpts}
                          value={contaValue}
                          onChange={(opt) => {
                            formik.setFieldValue(
                              'conta_imobiliaria',
                              opt ? opt.value : ''
                            );
                          }}
                          styles={selectStylesComPortal}
                          menuPortalTarget={document.body}
                          menuPosition="fixed"
                          placeholder="Selecione uma conta..."
                          isClearable
                          noOptionsMessage={() => 'Nenhuma conta disponível'}
                        />
                        {formik.touched.conta_imobiliaria &&
                          formik.errors.conta_imobiliaria && (
                            <div
                              className="text-danger"
                              style={{ fontSize: '0.75rem' }}
                            >
                              {formik.errors.conta_imobiliaria}
                            </div>
                          )}
                      </Form.Group>
                    </Col>
                  </Row>
                </fieldset>

                {/* Logo */}
                <fieldset
                  className="border rounded px-3 pb-2 pt-1 mb-3"
                  style={{ borderColor: '#dee2e6' }}
                >
                  <legend
                    className="w-auto px-2 mb-0"
                    style={{
                      fontSize: '0.8rem',
                      fontWeight: 'bold',
                      color: '#495057',
                    }}
                  >
                    Logo (opcional, PNG ou JPG, máx. 6 MB)
                  </legend>
                  <Row className="align-items-center">
                    <Col md={7}>
                      <Form.Group className="mb-1">
                        <Form.Control
                          type="file"
                          accept=".png,.jpg,.jpeg,image/png,image/jpeg"
                          size="sm"
                          onChange={(e) => {
                            const file =
                              e.target.files && e.target.files[0]
                                ? e.target.files[0]
                                : null;
                            handleSelecionarLogo(file, formik);
                          }}
                        />
                        <ErrorMessage
                          name="logo"
                          component="div"
                          className="invalid-feedback d-block"
                          style={{ fontSize: '0.75rem' }}
                        />
                        {ehEdicao && (
                          <small
                            className="text-muted d-block mt-1"
                            style={{ fontSize: '0.75rem' }}
                          >
                            Ação atual:{' '}
                            <strong>
                              {acaoLogo === 'manter' && 'manter logo atual'}
                              {acaoLogo === 'substituir' && 'substituir logo'}
                              {acaoLogo === 'remover' && 'remover logo'}
                            </strong>
                          </small>
                        )}
                      </Form.Group>
                    </Col>
                    <Col md={5} className="text-center">
                      {logoPreviewUrl ? (
                        <div>
                          <img
                            src={logoPreviewUrl}
                            alt="Preview"
                            style={{
                              maxHeight: 60,
                              maxWidth: '100%',
                              objectFit: 'contain',
                              border: '1px solid #dee2e6',
                              borderRadius: 4,
                              padding: 4,
                              backgroundColor: '#fff',
                            }}
                          />
                          <Button
                            size="sm"
                            variant="outline-danger"
                            className="ml-2"
                            onClick={() => {
                              handleRemoverLogo(formik);
                            }}
                            title="Remover logo"
                          >
                            <FaTrash />
                          </Button>
                        </div>
                      ) : (
                        <small className="text-muted">Sem logo</small>
                      )}
                    </Col>
                  </Row>
                </fieldset>

                {/* Status */}
                <fieldset
                  className="border rounded px-3 pb-2 pt-1 mb-2"
                  style={{ borderColor: '#dee2e6' }}
                >
                  <legend
                    className="w-auto px-2 mb-0"
                    style={{
                      fontSize: '0.8rem',
                      fontWeight: 'bold',
                      color: '#495057',
                    }}
                  >
                    Status
                  </legend>
                  <Row>
                    <Col md={6}>
                      <Form.Check
                        type="switch"
                        id="switch-ativo"
                        label="Imobiliária ativa"
                        checked={!!formik.values.ativo}
                        onChange={(e) => {
                          formik.setFieldValue('ativo', e.target.checked);
                        }}
                      />
                    </Col>
                    <Col md={6}>
                      <Form.Check
                        type="switch"
                        id="switch-ranking"
                        label="Aparece no Ranking de Vendas"
                        checked={!!formik.values.ranking_venda}
                        onChange={(e) => {
                          formik.setFieldValue(
                            'ranking_venda',
                            e.target.checked
                          );
                        }}
                      />
                    </Col>
                  </Row>
                </fieldset>
              </Modal.Body>

              <Modal.Footer>
                <Button
                  variant="secondary"
                  onClick={onHide}
                  disabled={formik.isSubmitting}
                >
                  Cancelar
                </Button>
                <Button
                  variant="primary"
                  type="submit"
                  disabled={formik.isSubmitting}
                >
                  {formik.isSubmitting ? 'Salvando...' : 'Salvar'}
                </Button>
              </Modal.Footer>
            </Form>
          );
        }}
      </Formik>
    </Modal>
  );
};

export default ModalImobiliaria;
